import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { VStack, HStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import type { Availability, Service } from '@/lib/types';
import { addDays, dayLabel, todayIn } from '@/lib/format';
import { cn } from '@/lib/utils';

const PAGE_DAYS = 14;

export function SlotPicker({
  service,
  timezone,
  horizonDays,
  load,
  value,
  onChange,
  notice,
}: {
  service: Service;
  timezone: string;
  horizonDays: number;
  load: (serviceId: string, from: string, days: number) => Promise<Availability>;
  value: string | null;
  onChange: (startsAt: string) => void;
  notice?: string;
}) {
  const today = todayIn(timezone);
  const [from, setFrom] = useState(today);
  const q = useQuery({
    queryKey: ['availability', service.id, from],
    queryFn: () => load(service.id, from, PAGE_DAYS),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const days = q.data?.days ?? [];
  const firstWithSlots = days.find((d) => d.slots.some((s) => s.available))?.date;
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const day = selectedDay ?? firstWithSlots ?? days[0]?.date ?? null;
  const slots = useMemo(() => days.find((d) => d.date === day)?.slots ?? [], [days, day]);

  // a selected time that has just been taken is dropped
  useEffect(() => {
    if (value && q.data && !q.data.days.some((d) => d.slots.some((s) => s.starts_at === value && s.available))) {
      onChange('');
    }
  }, [q.data, value, onChange]);

  const canNext = addDays(from, PAGE_DAYS) <= addDays(today, horizonDays);
  const multiDay = service.duration_minutes >= 1440;

  return (
    <VStack gap={4}>
      {notice && <Banner status="warning" title={notice} />}
      {multiDay && (
        <Text type="supporting">
          Работа длится несколько дней: выберите время приёма автомобиля, бокс будет занят до готовности.
        </Text>
      )}

      <VStack gap={2}>
        <Text type="label" weight="semibold">
          Дата
        </Text>
        <ul className="scroll-snap-x -mx-4 flex scroll-px-4 gap-2 overflow-x-auto px-4 pb-1" role="listbox" aria-label="Выбор даты">
          {q.isPending &&
            Array.from({ length: 6 }, (_, i) => (
              <li key={i} className="shrink-0">
                <Skeleton width={72} height={64} radius={3} index={i} />
              </li>
            ))}
          {days.map((d) => {
            const free = d.slots.filter((s) => s.available).length;
            const selected = d.date === day;
            const [wd, ...rest] = dayLabel(d.date, timezone).split(', ');
            return (
              <li key={d.date} className="shrink-0">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={`${dayLabel(d.date, timezone)}: ${free ? `свободно ${free}` : d.slots.length ? 'всё занято' : 'не работаем'}`}
                  onClick={() => setSelectedDay(d.date)}
                  className={cn(
                    'flex min-h-16 min-w-18 flex-col items-center justify-center rounded-lg border px-3 py-2 transition-colors duration-150',
                    selected ? 'border-accent bg-accent-bg text-on-accent' : 'border-border bg-card text-primary',
                    !free && !selected && 'opacity-55',
                  )}
                >
                  <span className="text-xs capitalize">{wd}</span>
                  <span className="text-base font-semibold">{rest.join(', ')}</span>
                  <span className="text-xs">{free ? `${free} мест` : d.slots.length ? 'занято' : 'выходной'}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <HStack gap={2}>
          {from > today && <Button label="Раньше" size="sm" variant="ghost" onClick={() => { setFrom(addDays(from, -PAGE_DAYS)); setSelectedDay(null); }} />}
          {canNext && <Button label="Позже" size="sm" variant="ghost" onClick={() => { setFrom(addDays(from, PAGE_DAYS)); setSelectedDay(null); }} />}
        </HStack>
      </VStack>

      <VStack gap={2}>
        <Text type="label" weight="semibold">
          Время
        </Text>
        {q.isError && (
          <Banner
            status="error"
            title="Не удалось загрузить расписание"
            description={(q.error as Error).message}
            endContent={<Button label="Повторить" size="sm" onClick={() => q.refetch()} />}
          />
        )}
        {q.isPending && <Skeleton height={96} radius={3} />}
        {!q.isPending && slots.length === 0 && (
          <EmptyState isCompact title="В этот день студия не принимает" description="Выберите другую дату." />
        )}
        {slots.length > 0 && (
          <ul className="grid grid-cols-4 gap-2 sm:grid-cols-5" aria-label="Свободное время">
            {slots.map((s) => {
              const selected = s.starts_at === value;
              return (
                <li key={s.starts_at}>
                  <button
                    type="button"
                    disabled={!s.available}
                    aria-pressed={selected}
                    aria-label={s.available ? `${s.time}, свободно` : `${s.time}, занято`}
                    onClick={() => onChange(s.starts_at)}
                    className={cn(
                      'flex min-h-11 w-full flex-col items-center justify-center rounded-md border text-sm font-medium tabular-nums transition-colors duration-150',
                      selected && 'border-accent bg-accent-bg text-on-accent',
                      !selected && s.available && 'border-border bg-card text-primary hover:border-accent',
                      !s.available && 'cursor-not-allowed border-transparent bg-muted text-disabled',
                    )}
                  >
                    <span className={cn(!s.available && 'slot-busy')}>{s.time}</span>
                    {!s.available && <span className="text-xs leading-none">занято</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </VStack>
    </VStack>
  );
}
